/**
 * Public API (§56) – participant facing. Responses contain no internal IDs, no LearningSuite
 * group ID and no database fields beyond what the UI needs (§14, §50, §56). All response schemas
 * are `.strict()` so tests can assert that nothing internal leaks.
 */
import { z } from 'zod';
import { AppEnvironmentSchema, RegistrationStatus } from '../enums';
import { PROVISIONING_DELAYED_AFTER_ATTEMPTS } from '../constants';
import {
  ActivationCodeInputSchema,
  EmailInputSchema,
  IsoDateTimeSchema,
  NullableIsoDateTimeSchema,
  OpaqueTokenSchema,
  PersonNameSchema,
} from './common';

// --- GET /api/v1/public/config ----------------------------------------------------------------

/** Runtime configuration of the SPA – domains are never baked into the build. */
export const PublicConfigResponseSchema = z
  .object({
    environment: AppEnvironmentSchema,
    /** e.g. `https://zugang.brotgenau-cockpit.de` */
    publicBaseUrl: z.url(),
    /** e.g. `https://zugang.brotgenau-cockpit.de/admin` */
    adminBaseUrl: z.url(),
    /** Host shown in letters and UI, e.g. `zugang.brotgenau-cockpit.de`. */
    activationDomain: z.string(),
    privacyPolicyUrl: z.url(),
    imprintUrl: z.url().nullable(),
    turnstileSiteKey: z.string(),
  })
  .strict();
export type PublicConfigResponse = z.infer<typeof PublicConfigResponseSchema>;

// --- shared participant view ------------------------------------------------------------------

/**
 * Public projection of RegistrationStatus. Internal failure states are not exposed:
 * PROVISIONING_FAILED and repeated retries are shown as „Einrichtung verzögert sich“ (§53).
 */
export const PublicRegistrationState = {
  PENDING_EMAIL: 'PENDING_EMAIL',
  PROVISIONING: 'PROVISIONING',
  PROVISIONING_DELAYED: 'PROVISIONING_DELAYED',
  ACTIVE: 'ACTIVE',
  EXPIRED: 'EXPIRED',
  CANCELLED: 'CANCELLED',
} as const;
export type PublicRegistrationState =
  (typeof PublicRegistrationState)[keyof typeof PublicRegistrationState];
export const PublicRegistrationStateSchema = z.enum(PublicRegistrationState);

/** Maps the internal registration status to the public state shown to participants. */
export function toPublicRegistrationState(
  status: RegistrationStatus,
  provisioningAttemptCount: number,
): PublicRegistrationState {
  switch (status) {
    case RegistrationStatus.PENDING_EMAIL:
      return PublicRegistrationState.PENDING_EMAIL;
    case RegistrationStatus.EMAIL_VERIFIED:
      return PublicRegistrationState.PROVISIONING;
    case RegistrationStatus.PROVISIONING:
      return provisioningAttemptCount >= PROVISIONING_DELAYED_AFTER_ATTEMPTS
        ? PublicRegistrationState.PROVISIONING_DELAYED
        : PublicRegistrationState.PROVISIONING;
    case RegistrationStatus.PROVISIONING_FAILED:
      return PublicRegistrationState.PROVISIONING_DELAYED;
    case RegistrationStatus.ACTIVE:
      return PublicRegistrationState.ACTIVE;
    case RegistrationStatus.EXPIRED:
      return PublicRegistrationState.EXPIRED;
    case RegistrationStatus.CANCELLED:
      return PublicRegistrationState.CANCELLED;
  }
}

/** Status of the registration bound to the browser's registration session (§16, §51, §52). */
export const PublicRegistrationStatusSchema = z
  .object({
    state: PublicRegistrationStateSchema,
    /** `m***@gmail.com` (§51) */
    maskedEmail: z.string(),
    /** Company display name (§50); null if not configured. */
    companyName: z.string().nullable(),
    /** Only while PENDING_EMAIL. */
    verificationExpiresAt: NullableIsoDateTimeSchema,
    /** Earliest time a resend is allowed (§20); null if not PENDING_EMAIL or limit reached. */
    resendAvailableAt: NullableIsoDateTimeSchema,
    /** False when the per-registration email limit is reached. */
    canResend: z.boolean(),
    /** False when the per-registration email-change limit is reached or not PENDING_EMAIL. */
    canChangeEmail: z.boolean(),
    /** The provider reported a hard bounce – UI suggests correcting the address (§31). */
    emailBounced: z.boolean(),
  })
  .strict();
export type PublicRegistrationStatus = z.infer<typeof PublicRegistrationStatusSchema>;

export const PublicRegistrationResponseSchema = z
  .object({ registration: PublicRegistrationStatusSchema })
  .strict();
export type PublicRegistrationResponse = z.infer<typeof PublicRegistrationResponseSchema>;

// --- GET /api/v1/public/activation/:code (§14) ------------------------------------------------

export const ActivationCodeParamsSchema = z.object({ code: ActivationCodeInputSchema });
export type ActivationCodeParams = z.infer<typeof ActivationCodeParamsSchema>;

export const ActivationCheckState = {
  /** Code can be activated – show the form (§50). */
  AVAILABLE: 'AVAILABLE',
  /**
   * The browser's registration session belongs to the current registration of this code
   * (pending or later) – show the status page instead of the form.
   */
  OWN_REGISTRATION: 'OWN_REGISTRATION',
} as const;
export type ActivationCheckState = (typeof ActivationCheckState)[keyof typeof ActivationCheckState];
export const ActivationCheckStateSchema = z.enum(ActivationCheckState);

/**
 * Success response of the code check. All negative outcomes are errors:
 * ACTIVATION_CODE_INVALID | _USED | _DISABLED | _EXPIRED | BATCH_DISABLED |
 * ACTIVATION_ALREADY_PENDING | TURNSTILE_REQUIRED | RATE_LIMITED.
 */
export const ActivationCheckResponseSchema = z
  .object({
    /** Normalised, formatted code `XXXXX-XXXXX` (echo of the input). */
    code: z.string(),
    state: ActivationCheckStateSchema,
    companyName: z.string().nullable(),
  })
  .strict();
export type ActivationCheckResponse = z.infer<typeof ActivationCheckResponseSchema>;

// --- POST /api/v1/public/activation/:code/start (§15) -----------------------------------------

export const StartRegistrationRequestSchema = z
  .object({
    firstName: PersonNameSchema,
    lastName: PersonNameSchema,
    email: EmailInputSchema,
    turnstileToken: z.string().min(1).max(2048),
  })
  .strict();
export type StartRegistrationRequest = z.input<typeof StartRegistrationRequestSchema>;
export type StartRegistrationInput = z.output<typeof StartRegistrationRequestSchema>;
/** 201 + `Set-Cookie: __Host-lsc_reg=…` */
export const StartRegistrationResponseSchema = PublicRegistrationResponseSchema;

// --- POST /api/v1/public/registration/resend-verification (§20) -------------------------------

export const ResendVerificationRequestSchema = z
  .object({ turnstileToken: z.string().min(1).max(2048) })
  .strict();
export type ResendVerificationRequest = z.infer<typeof ResendVerificationRequestSchema>;
export const ResendVerificationResponseSchema = PublicRegistrationResponseSchema;

// --- PATCH /api/v1/public/registration/email (§19) --------------------------------------------

export const ChangeEmailRequestSchema = z
  .object({
    email: EmailInputSchema,
    turnstileToken: z.string().min(1).max(2048),
  })
  .strict();
export type ChangeEmailRequest = z.input<typeof ChangeEmailRequestSchema>;
export const ChangeEmailResponseSchema = PublicRegistrationResponseSchema;

// --- POST /api/v1/public/verify (§17, §21) ----------------------------------------------------

/**
 * The SPA page `/verify?t=…` reads the token, removes it from the URL and POSTs it here
 * (no state change on GET – mail scanners must not consume tokens).
 * On success the server (re-)issues the registration session cookie, because the link is often
 * opened in a different browser than the one that started the registration.
 */
export const VerifyEmailRequestSchema = z.object({ token: OpaqueTokenSchema }).strict();
export type VerifyEmailRequest = z.infer<typeof VerifyEmailRequestSchema>;
export const VerifyEmailResponseSchema = PublicRegistrationResponseSchema;

// --- GET /api/v1/public/registration/status (§52) ---------------------------------------------

export const RegistrationStatusResponseSchema = PublicRegistrationResponseSchema;

// --- GET /health, /ready (§67) ----------------------------------------------------------------

export const HealthResponseSchema = z.object({
  status: z.literal('ok'),
  time: IsoDateTimeSchema,
});
export type HealthResponse = z.infer<typeof HealthResponseSchema>;

export const ReadyResponseSchema = z.object({
  status: z.enum(['ready', 'not_ready']),
  checks: z.object({
    database: z.enum(['ok', 'error']),
  }),
  time: IsoDateTimeSchema,
});
export type ReadyResponse = z.infer<typeof ReadyResponseSchema>;
